import React, { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Fade } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';

const defaultDurations = {
  success: 3000,
  info: 3000,
  warning: 3000,
  error: 3000
};

const getA11y = (type) => {
  if (type === 'error') return { role: 'alert', live: 'assertive' };
  return { role: 'status', live: 'polite' };
};

const AlertDialog = ({
  open,
  onClose,
  title,
  message,
  type = 'info',
  confirm = false,
  onConfirm,
  autoCloseMs,
  persistent = false
}) => {
  useEffect(() => {
    if (!open) return;
    if (confirm) return;
    if (persistent) return;
    const ms = typeof autoCloseMs === 'number' ? autoCloseMs : defaultDurations[type] || 3000;
    const t = setTimeout(() => onClose && onClose(), ms);
    return () => clearTimeout(t);
  }, [open, confirm, persistent, autoCloseMs, type, onClose]);

  const a11y = getA11y(type);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {type === 'error' && <ErrorIcon color="error" />}
        {type === 'warning' && <WarningIcon color="warning" />}
        {type === 'success' && <CheckCircleIcon color="success" />}
        {type === 'info' && <InfoIcon color="info" />}
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography
          id="alert-dialog-description"
          sx={{ whiteSpace: 'pre-line' }}
          role={a11y.role}
          aria-live={a11y.live}
        >
          {message}
        </Typography>
      </DialogContent>
      {confirm ? (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AlertDialog;
